import { useFeatureValue } from '@growthbook/growthbook-react';
import {
  CheckCircleRounded,
  CloseRounded,
  KeyboardArrowUpRounded,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Lottie from 'lottie-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { confettiAnimation, emptyComplete } from '@/assets';
import {
  ComplianceTypes,
  OnboardingChecklistStepOrder,
  Routes,
  RoutesWithPlayer,
} from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  selectActiveChapterId,
  selectBookmarkedEpisodeIds,
  selectCreditEarnedSessions,
  selectPlayerSessionsResult,
  selectSessionUserId,
  selectUserCompliance,
  selectUserProfileResult,
  useFetchUserUIStatesQuery,
  useUpdateUserUIStateMutation,
} from '@/store';
import { appStyles } from '@/theme';
import { OnboardingExperienceFlag } from '@/types';
import { createRouteWithParams } from '@/utils';

import { Link } from '../../../Link';

type ListStep = {
  title: string;
  description: string;
  ctaLabel: string | React.ReactNode;
  ctaLink?: string;
  ctaOnClick?: () => void;
};

const SCREEN_PADDING = 24;
const COLLAPSED_HEIGHT = 95;
const STEP_TRANSITION_DURATION = 700;
const RECOMMENDED_EPISODE = {
  title: "Disney's Acquisition of Pixar",
  id: 'OEtflqiHqAP0XLqJWkt2',
};

const referrer = {
  component: EVENT_CONSTANTS.COMPONENT.ONBOARDING_CHECKLIST,
};

export const useIsOnboardingChecklistVisible = () => {
  const { experience: onboardingExperience, cutoffDate } =
    useFeatureValue<OnboardingExperienceFlag>('onboarding-experience', {});

  const isFeatureEnabled = onboardingExperience === 'checklist';
  const isMdDown = useMediaQuery(theme => theme.breakpoints.down('md'));

  // Hide checklist on some routes - the non-player routes list lines up well with this
  const { pathname } = useLocation();
  const isPlayerRoute = useMemo(() => {
    return Object.values(RoutesWithPlayer).some(route =>
      matchPath(route, pathname),
    );
  }, [pathname]);

  const userId = useSelector(selectSessionUserId);
  const { data: userProfile } = useSelector(selectUserProfileResult);
  const { data: userUIStates } = useFetchUserUIStatesQuery(undefined, {
    skip: !userId,
  });
  const isUserCreatedAfterChecklistLaunch =
    userProfile?.userCreatedAt &&
    (!cutoffDate || new Date(userProfile.userCreatedAt) > new Date(cutoffDate));
  const isChecklistCompleted =
    !!userUIStates?.onboardingChecklistProgress?.isChecklistCompleted;

  if (
    !userUIStates ||
    !isUserCreatedAfterChecklistLaunch ||
    !isFeatureEnabled ||
    isChecklistCompleted ||
    !isPlayerRoute ||
    isMdDown
  ) {
    return false;
  }

  return true;
};

export const OnboardingChecklist = () => {
  const location = useLocation();

  const compliance = useSelector(selectUserCompliance);
  const activeChapterId = useSelector(selectActiveChapterId);
  const bookmarkedEpisodeIds = useSelector(selectBookmarkedEpisodeIds);
  const { data: sessions } = useSelector(selectPlayerSessionsResult);
  const creditEarnedSessions = useSelector(selectCreditEarnedSessions);

  const [isExpanded, setIsExpanded] = useState(false);

  const { data: userUIStates } = useFetchUserUIStatesQuery();

  const [updateUserUIState] = useUpdateUserUIStateMutation();

  const steps = useMemo<ListStep[]>(() => {
    return [
      {
        title: 'Start your first episode',
        description:
          'Press play on any episode. This is how your journey begins.',
        ctaLabel: (
          <span>
            Listen to{' '}
            <span style={{ fontStyle: 'italic' }}>
              {RECOMMENDED_EPISODE.title}
            </span>
          </span>
        ),
        ctaLink: createRouteWithParams(Routes.EPISODE, {
          episodeId: RECOMMENDED_EPISODE.id,
        }),
      },
      {
        title: 'Earn compliance on the episode',
        description:
          compliance === ComplianceTypes.CPD
            ? 'Pass the quiz at the end of the chapter to earn CPD hours. Learning that counts, automatically.'
            : 'Pass the qualified assessment after listening to the episode to earn CPE credits. Learning that counts, automatically.',
        ctaLabel: `Earn ${compliance === ComplianceTypes.CPD ? 'CPD' : 'CPE'}`,
        ctaLink:
          compliance === ComplianceTypes.CPD
            ? `${Routes.MY_LUMIQ}?tab=quizzes`
            : `${Routes.MY_LUMIQ}?tab=assessments`,
      },
      {
        title: 'Check your tracker',
        description: `See your ${compliance === ComplianceTypes.CPD ? 'hours' : 'credits'} add up in real time. Every episode moves you closer to your goal.`,
        ctaLabel: 'View tracker',
        ctaLink: Routes.TRACKER,
      },
    ];
  }, [compliance]);

  const currentStep =
    userUIStates?.onboardingChecklistProgress?.currentStep ?? 0;
  const totalSteps = steps.length;

  const stepRef = useRef(currentStep);

  const progressPercentage = (currentStep / totalSteps) * 100;

  useEffect(() => {
    // Track step completion
    const advanceStep = async () => {
      setTimeout(
        () =>
          updateUserUIState({
            key: 'onboardingChecklistProgress',
            value: {
              isChecklistCompleted: false,
              currentStep: currentStep + 1,
            },
          }),
        500,
      );
    };

    if (currentStep === OnboardingChecklistStepOrder.START_EPISODE) {
      // Check if started sessions list has items
      const startedEpisodes = Object.values(sessions?.entities ?? {}).filter(
        session => session.status !== 'none',
      );
      if (startedEpisodes.length > 0) advanceStep();
    } else if (currentStep === OnboardingChecklistStepOrder.EARN_COMPLIANCE) {
      // Check if credits list has items
      if (creditEarnedSessions?.length > 0) advanceStep();
    } else if (currentStep === OnboardingChecklistStepOrder.CHECK_TRACKER) {
      // Check if current route is Tracker
      if (location.pathname === Routes.TRACKER) advanceStep();
    }

    // Bookmark step progress check - keeping it here in case we switch the Share step back to Bookmark
    // } else if (currentStep === StepOrder.BOOKMARK_EPISODE) {
    //   // Check if bookmarked episode list has items
    //   if (bookmarkedEpisodeIds && bookmarkedEpisodeIds.length > 0)
    //     advanceStep();
    // }
  }, [
    currentStep,
    sessions,
    creditEarnedSessions,
    updateUserUIState,
    location,
    bookmarkedEpisodeIds,
  ]);

  useEffect(() => {
    // Expand the list and log analytics event every time there is progress
    if (stepRef.current < currentStep) {
      AnalyticsService.logEvent(EVENTS.GET_STARTED_CHECKLIST_EVENTS.STEP, {
        completed_step_name: steps[stepRef.current].title,
        completed_step_id: stepRef.current,
      });
      setIsExpanded(true);
    }
    stepRef.current = currentStep;
  }, [currentStep, steps]);

  const onToggle = async () => {
    if (currentStep >= totalSteps && isExpanded) {
      await updateUserUIState({
        key: 'onboardingChecklistProgress',
        value: {
          isChecklistCompleted: true,
          currentStep: totalSteps,
        },
      });
      AnalyticsService.logEvent(EVENTS.GET_STARTED_CHECKLIST_EVENTS.FINISH);

      return;
    }

    AnalyticsService.logEvent(
      isExpanded
        ? EVENTS.GET_STARTED_CHECKLIST_EVENTS.VIEW_COLLAPSE
        : EVENTS.GET_STARTED_CHECKLIST_EVENTS.VIEW_EXPAND,
    );

    setIsExpanded(prev => !prev);
  };

  return (
    <Box
      sx={[
        appStyles.featureAnnouncement,
        {
          width: 350,
          bottom: theme =>
            (activeChapterId ? theme.dimensions.miniPlayer.height.sm : 0) +
            SCREEN_PADDING,
        },
      ]}>
      <Box
        onClick={() => {
          if (isExpanded) return;
          onToggle();
        }}
        sx={[
          styles.persistentSection,
          { cursor: isExpanded ? 'default' : 'pointer' },
        ]}>
        <Box sx={styles.persistentContents}>
          <Box>
            <Typography
              variant="caption1Bold"
              sx={{ color: 'secondary.main', mb: 0.5 }}>
              GET STARTED
            </Typography>
            <Typography variant="body2SemiBold" sx={{ mb: 1.5 }}>
              {`${currentStep} of ${totalSteps} complete`}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (!isExpanded) return;
              onToggle();
            }}
            sx={[
              styles.expandCollapseButton,
              { transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' },
            ]}>
            {currentStep >= totalSteps && isExpanded ? (
              <CloseRounded sx={{ color: 'secondary.main', fontSize: 22 }} />
            ) : (
              <KeyboardArrowUpRounded
                sx={{ color: 'secondary.main', fontSize: 28 }}
              />
            )}
          </IconButton>
        </Box>
        <Box sx={styles.progressContainer}>
          <Box
            sx={[styles.progressContent, { width: `${progressPercentage}%` }]}
          />
        </Box>
      </Box>
      <Box
        sx={[
          styles.listContainer,
          {
            height: isExpanded ? 'auto' : 0,
            maxHeight: theme =>
              window.innerHeight -
              theme.dimensions.header.height -
              (activeChapterId ? theme.dimensions.miniPlayer.height.xs : 0) -
              COLLAPSED_HEIGHT -
              SCREEN_PADDING * 2,
          },
        ]}>
        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              borderBottom:
                index < currentStep || index === steps.length - 1
                  ? 'none'
                  : '2px solid',
              borderColor: 'grey50',
              display: 'flex',
              gap: 2,
              backgroundColor:
                index <= currentStep ? 'secondary.light' : 'transparent',
            }}>
            {index < currentStep ? (
              <Box
                style={{
                  position: 'relative',
                  width: 22,
                  height: 22,
                  zIndex: index,
                }}>
                {index < currentStep && index !== steps.length - 1 && (
                  <Box
                    sx={[
                      styles.connectingLine,
                      { animation: `grow ${STEP_TRANSITION_DURATION}ms ease` },
                    ]}
                  />
                )}
                <CheckCircleRounded
                  sx={{
                    fontSize: 22,
                    color: 'secondary.main',
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  zIndex: index,
                  height: 19,
                  width: 19,
                  m: '1.5px',
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor:
                    index <= currentStep ? 'secondary.light' : 'transparent',
                  border: theme =>
                    `3px solid ${index === currentStep ? theme.palette.secondary.main : theme.palette.text.trace}}`,
                }}
              />
            )}
            <Box>
              <Typography
                variant="body2SemiBold"
                sx={[
                  index < currentStep && {
                    color: 'secondary.main',
                    textDecoration: 'line-through',
                  },
                  index === currentStep && {
                    color: 'text.main',
                  },
                  index > currentStep && {
                    color: 'text.trace',
                  },
                ]}>
                {step.title}
              </Typography>
              {index === currentStep && (
                <>
                  <Typography variant="body3" color="text.main" sx={{ my: 1 }}>
                    {step.description}
                  </Typography>
                  <Link
                    textVariant="body2SemiBold"
                    to={step.ctaLink}
                    state={{ referrer }}
                    onClick={() => {
                      AnalyticsService.logEvent(
                        EVENTS.GET_STARTED_CHECKLIST_EVENTS.CTA_CLICK,
                        {
                          step_name: steps[currentStep].title,
                          step_id: stepRef.current,
                        },
                      );
                      step.ctaOnClick?.();
                    }}>
                    <Typography variant="body3SemiBold">
                      {step.ctaLabel}
                    </Typography>
                  </Link>
                </>
              )}
            </Box>
          </Box>
        ))}
        {currentStep >= totalSteps && (
          <Box sx={styles.completedContainer}>
            <Box sx={styles.completedContainerLeft}>
              <Lottie
                animationData={confettiAnimation}
                loop={false}
                style={{
                  width: 180,
                  height: 180,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: 'translateX(-62px) translateY(-51px)',
                  pointerEvents: 'none',
                }}
              />
              <Box
                component="img"
                src={emptyComplete}
                alt="Checklist completed"
                sx={styles.completedImage}
              />
            </Box>
            <Box>
              <Typography variant="body2SemiBold">
                You&apos;re all set!
              </Typography>
              <Typography variant="body3" color="text.main" sx={{ mt: 1 }}>
                That&apos;s the gold standard in {compliance}, right there. Your{' '}
                {compliance === ComplianceTypes.CPD ? 'hours' : 'credits'} are
                tracking and your next episode is waiting.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const styles = {
  persistentSection: {
    px: 2,
    bgcolor: (theme: Theme) => `${theme.palette.secondary.medium}E4`,
    backdropFilter: 'blur(14px)',
    height: COLLAPSED_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  persistentContents: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  expandCollapseButton: {
    p: 1,
    mr: -1,
    cursor: 'pointer',
    transition: 'transform 250ms ease',
  },
  progressContainer: {
    height: 10,
    width: '100%',
    backgroundColor: 'white',
    overflow: 'hidden',
    borderRadius: 3,
  },
  progressContent: {
    height: '100%',
    backgroundColor: 'secondary.main',
    borderRadius: 3,
    transition: 'width 700ms ease',
  },
  listContainer: {
    interpolateSize: 'allow-keywords',
    transition: 'height 250ms ease',
    backgroundColor: '#FFFFFFE4',
    backdropFilter: 'blur(14px)',
    overflow: 'scroll',
  },
  connectingLine: {
    backgroundColor: 'secondary.main',
    width: 3,
    height: 38,
    position: 'absolute',
    top: 20,
    left: 'calc(50% - 1.5px)',
    '@keyframes grow': {
      '0%': { height: 0 },
      '100%': { height: 38 },
    },
  },
  completedContainer: {
    px: 3,
    pb: 3,
    pt: 1,
    bgcolor: 'secondary.light',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
    overflow: 'hidden',
  },
  completedContainerLeft: {
    position: 'relative',
    width: 60,
    height: 60,
    flexShrink: 0,
  },
  animation: {
    width: 180,
    height: 180,
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'translateX(-62px) translateY(-51px)',
    pointerEvents: 'none',
  },
  completedImage: {
    width: 60,
    height: 60,
    position: 'absolute',
    left: -8,
    top: 0,
  },
};
